import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3, Users, FileText, TrendingUp, CheckCircle2, Clock, XCircle, Edit3,
  BookOpen, AlertCircle, Brain, Layers,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useT } from '@/store/i18n';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs } from '@/components/ui/Tabs';
import { Select } from '@/components/ui/Select';
import { api } from '@/lib/api';

// ─── Cognitive report types ───────────────────────────────────────────────────

interface CognitiveSubjectCell {
  subject_name: string;
  subject_code: string;
  total_questions: number;
  approved: number;
  pending: number;
  draft: number;
  rejected: number;
}

interface CognitiveSummaryRow {
  id: number;
  name: string;
  description: string;
  level_order: number;
  total_questions: number;
  approved: number;
  pending: number;
  draft: number;
  rejected: number;
}

interface CognitiveMatrixRow {
  cognitive_id: number;
  cognitive_name: string;
  level_order: number;
  subjects: Record<string, CognitiveSubjectCell>;
  total: number;
}

interface CognitiveData {
  matrix: CognitiveMatrixRow[];
  cognitive_summary: CognitiveSummaryRow[];
  subject_groups: Record<string, { name: string; code: string }>;
  cognitive_totals: Record<string, number>;
  subject_totals: Record<string, number>;
  unspecified_cognitive: { total_questions: number };
}

// ─── Difficulty report types ──────────────────────────────────────────────────

interface DifficultyGradeCell {
  grade_name: string;
  total_questions: number;
  approved: number;
  pending: number;
  draft: number;
  rejected: number;
}

interface DifficultyMatrixRow {
  difficulty_level: string;
  difficulty_name: string;
  grades: Record<string, DifficultyGradeCell>;
  total: number;
}

interface DifficultySummaryRow {
  difficulty_level: string;
  difficulty_name: string;
  total_questions: number;
  approved: number;
  pending: number;
  draft: number;
  rejected: number;
}

interface DifficultyData {
  matrix: DifficultyMatrixRow[];
  difficulty_summary: DifficultySummaryRow[];
  grade_names: Record<string, string>;
  difficulty_totals: Record<string, number>;
  grade_totals: Record<string, number>;
}

// ─── Indicator × Grade types ──────────────────────────────────────────────────

interface IndicatorForSubject {
  id: number;
  code: string;
  title: string;
  subject_id: number;
  subject_name: string;
  grade_id: number;
  grade_name: string;
  questionCount: number;
  approved_questions: number;
}

interface SubjectIndicatorGroup {
  id: number;
  name: string;
  code: string;
  indicators: IndicatorForSubject[];
  totalQuestions: number;
  totalIndicators: number;
  indicatorsWithQuestions: number;
  hasData: boolean;
}

interface IndicatorGradeData {
  bySubject: Record<string, SubjectIndicatorGroup>;
  allIndicators: IndicatorForSubject[];
  statistics: {
    total_indicators: number;
    total_questions: number;
    overall_coverage: number;
  };
}

// ─── Coverage report types ────────────────────────────────────────────────────

interface CoverageIndicator {
  id: number;
  code: string;
  title: string;
  question_count: number;
}

interface CoverageCell {
  subject_group: { id: number; name: string };
  grade_level:   { id: number; name: string };
  indicators:    CoverageIndicator[];
  total:         number;
}

interface CoverageData {
  matrix:  CoverageCell[];
  summary: {
    total_indicators:     number;
    covered_indicators:   number;
    uncovered_indicators: number;
    total_questions:      number;
  };
}

interface MetaList {
  subject_groups?: Array<{ id: number; name: string }>;
  grade_levels?:   Array<{ id: number; name: string }>;
}

// ─── DashboardStats ───────────────────────────────────────────────────────────

interface DashboardStats {
  total_questions?: number;
  total_users?: number;
  questions_by_status?: { approved?: number; pending?: number; draft?: number; rejected?: number };
  questions_by_subject?: Array<{ id: number; name: string; count: number }>;
  questions_by_grade?: Array<{ id: number; name: string; count: number }>;
}

// STATUS_INFO labels resolved via t() inside the component

// ─── Curriculum Coverage Tab ──────────────────────────────────────────────────

function CoverageTab() {
  const t = useT();
  const navigate = useNavigate();
  const [subjectId, setSubjectId] = useState('');
  const [gradeId,   setGradeId]   = useState('');

  // Load filter options (subject groups + grade levels)
  const meta = useQuery({
    queryKey: ['metadata-full'],
    queryFn:  () =>
      api<{ data?: MetaList } & MetaList>('metadata/all.php').then((r) => r.data ?? r),
  });

  // Build query params
  const params = new URLSearchParams();
  if (subjectId) params.set('subject_group_id', subjectId);
  if (gradeId)   params.set('grade_level_id',   gradeId);
  const paramStr = params.toString();

  const coverage = useQuery({
    queryKey: ['coverage', subjectId, gradeId],
    queryFn: () =>
      api<{ data?: CoverageData } & CoverageData>(
        `reports/coverage.php${paramStr ? '?' + paramStr : ''}`
      ).then((r) => r.data ?? r),
    enabled: !!(subjectId || gradeId), // require at least one filter
  });

  const s = coverage.data?.summary;
  const matrix = coverage.data?.matrix ?? [];

  function countColor(n: number): string {
    if (n === 0) return 'bg-red-100 text-red-700';
    if (n <= 2)  return 'bg-amber-100 text-amber-700';
    return 'bg-green-100 text-green-700';
  }

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatTile
            icon={<BookOpen />}
            label={t('coverage.summary.total')}
            value={s.total_indicators}
            tone="blue"
          />
          <StatTile
            icon={<CheckCircle2 />}
            label={t('coverage.summary.covered')}
            value={s.covered_indicators}
            tone="brand"
          />
          <StatTile
            icon={<AlertCircle />}
            label={t('coverage.summary.uncovered')}
            value={s.uncovered_indicators}
            tone="red"
          />
          <StatTile
            icon={<FileText />}
            label={t('coverage.summary.questions')}
            value={s.total_questions}
            tone="violet"
          />
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select
            label={t('coverage.filter.subject')}
            value={subjectId}
            onChange={(e) => { setSubjectId(e.target.value); setGradeId(''); }}
          >
            <option value="">{t('filter.all_subjects')}</option>
            {(meta.data?.subject_groups ?? []).map((sg) => (
              <option key={sg.id} value={String(sg.id)}>{sg.name}</option>
            ))}
          </Select>
          <Select
            label={t('coverage.filter.grade')}
            value={gradeId}
            onChange={(e) => setGradeId(e.target.value)}
          >
            <option value="">{t('filter.all_grades')}</option>
            {(meta.data?.grade_levels ?? []).map((gl) => (
              <option key={gl.id} value={String(gl.id)}>{gl.name}</option>
            ))}
          </Select>
        </div>
      </Card>

      {/* Empty state: no filter selected */}
      {!subjectId && !gradeId && (
        <Card>
          <CardBody>
            <div className="py-12 text-center text-slate-500 text-sm">
              {t('coverage.empty_hint')}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Loading */}
      {coverage.isLoading && (
        <Card>
          <CardBody>
            <SkeletonBars />
          </CardBody>
        </Card>
      )}

      {/* Matrix */}
      {!coverage.isLoading && matrix.length > 0 && matrix.map((cell) => (
        <Card key={`${cell.subject_group.id}-${cell.grade_level.id}`}>
          <CardHeader>
            <CardTitle>
              {cell.subject_group.name}
              <span className="text-slate-400 font-normal mx-2">·</span>
              {cell.grade_level.name}
              <Badge tone="neutral" className="ml-2">{t('coverage.total_label')}: {cell.total}</Badge>
            </CardTitle>
          </CardHeader>
          <CardBody className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <th className="text-left px-3 py-2 font-semibold">{t('coverage.col.code')}</th>
                    <th className="text-left px-3 py-2 font-semibold">{t('coverage.col.title')}</th>
                    <th className="text-center px-3 py-2 font-semibold w-24">{t('coverage.col.count')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {cell.indicators.map((ind) => (
                    <tr
                      key={ind.id}
                      className="hover:bg-slate-50/70 cursor-pointer"
                      onClick={() =>
                        navigate(`/question-bank?learning_indicator_id=${ind.id}`)
                      }
                      title={t('coverage.row_hint')}
                    >
                      <td className="px-3 py-2 font-mono text-xs text-slate-600 whitespace-nowrap">
                        {ind.code}
                      </td>
                      <td className="px-3 py-2 text-slate-700 max-w-xs truncate">
                        {ind.title}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${countColor(ind.question_count)}`}>
                          {ind.question_count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ))}

      {/* No results after filtering */}
      {!coverage.isLoading && (subjectId || gradeId) && matrix.length === 0 && !coverage.isError && (
        <Card>
          <CardBody>
            <div className="py-10 text-center text-slate-500 text-sm">
              {t('coverage.no_indicators')}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Error */}
      {coverage.isError && (
        <Card>
          <CardBody>
            <div className="py-10 text-center text-red-500 text-sm">
              {t('error.load_failed')}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

// ─── Cognitive Tab ────────────────────────────────────────────────────────────

function CognitiveTab() {
  const t = useT();
  const navigate = useNavigate();

  const cognitive = useQuery({
    queryKey: ['report-cognitive'],
    queryFn: () =>
      api<{ data?: CognitiveData } & CognitiveData>('reports/by-cognitive.php').then(
        (r) => r.data ?? (r as unknown as CognitiveData)
      ),
  });

  const data = cognitive.data;
  const summary = data?.cognitive_summary ?? [];
  const matrix = data?.matrix ?? [];
  // Collect all subject_group ids present in the matrix (for column headers)
  const subjectGroupMap: Record<string, string> = {};
  matrix.forEach((row) => {
    Object.entries(row.subjects).forEach(([sid, cell]) => {
      subjectGroupMap[sid] = cell.subject_name;
    });
  });
  const subjectIds = Object.keys(subjectGroupMap);

  // Heat-map cell color: intensity based on cell count relative to max
  const allCounts = matrix.flatMap((r) =>
    Object.values(r.subjects).map((c) => c.total_questions)
  );
  const maxCount = Math.max(...allCounts, 1);

  function heatClass(n: number): string {
    if (n === 0) return 'bg-slate-50 text-slate-300';
    const ratio = n / maxCount;
    if (ratio >= 0.75) return 'bg-brand-600 text-white font-semibold';
    if (ratio >= 0.5)  return 'bg-brand-400 text-white';
    if (ratio >= 0.25) return 'bg-brand-200 text-brand-800';
    return 'bg-brand-50 text-brand-700';
  }

  return (
    <div className="space-y-5">
      {/* Bar chart — cognitive summary */}
      <Card>
        <CardHeader>
          <CardTitle>{t('reports.cognitive.title')}</CardTitle>
        </CardHeader>
        <CardBody className="pt-0">
          {cognitive.isLoading ? (
            <SkeletonBars />
          ) : summary.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">{t('reports.cognitive.no_data')}</div>
          ) : (
            <BarList
              items={summary.map((r) => ({ name: r.name, value: r.total_questions }))}
              accent="bg-violet-500"
            />
          )}
        </CardBody>
      </Card>

      {/* Heat-map matrix */}
      {!cognitive.isLoading && matrix.length > 0 && subjectIds.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('reports.cognitive.matrix_title')}</CardTitle>
          </CardHeader>
          <CardBody className="pt-0 overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 sticky left-0 bg-slate-50 min-w-[140px]">
                    {t('reports.cognitive.title').split(' ')[0]}
                  </th>
                  {subjectIds.map((sid) => (
                    <th key={sid} className="px-2 py-2 text-center text-slate-600 font-semibold min-w-[80px]">
                      <span className="block truncate max-w-[80px]" title={subjectGroupMap[sid]}>
                        {subjectGroupMap[sid]}
                      </span>
                    </th>
                  ))}
                  <th className="px-2 py-2 text-center text-slate-700 font-bold min-w-[60px]">
                    {t('reports.cognitive.col_total')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {matrix.map((row) => (
                  <tr key={row.cognitive_id} className="hover:bg-slate-50/50">
                    <td className="px-3 py-2.5 font-medium text-slate-700 sticky left-0 bg-white">
                      {row.cognitive_name}
                    </td>
                    {subjectIds.map((sid) => {
                      const cell = row.subjects[sid];
                      const count = cell?.total_questions ?? 0;
                      return (
                        <td key={sid} className="px-2 py-1.5 text-center">
                          <button
                            className={`w-full rounded px-1.5 py-1 text-xs transition-opacity hover:opacity-80 ${heatClass(count)}`}
                            title={count > 0 ? t('reports.cognitive.cell_hint') : undefined}
                            onClick={() => {
                              if (count > 0) {
                                navigate(`/question-bank?subject_group_id=${sid}`);
                              }
                            }}
                            disabled={count === 0}
                          >
                            {count}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-2 py-2.5 text-center font-bold text-slate-800">
                      {row.total}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Column totals row */}
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td className="px-3 py-2 font-bold text-slate-700 sticky left-0 bg-slate-50">
                    {t('reports.cognitive.col_total')}
                  </td>
                  {subjectIds.map((sid) => {
                    const total = matrix.reduce(
                      (sum, r) => sum + (r.subjects[sid]?.total_questions ?? 0),
                      0
                    );
                    return (
                      <td key={sid} className="px-2 py-2 text-center font-bold text-slate-700">
                        {total}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-center font-bold text-brand-700">
                    {matrix.reduce((s, r) => s + r.total, 0)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </CardBody>
        </Card>
      )}

      {/* Error */}
      {cognitive.isError && (
        <Card>
          <CardBody>
            <div className="py-8 text-center text-red-500 text-sm">{t('error.load_failed')}</div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

// ─── Difficulty Tab ───────────────────────────────────────────────────────────

function DifficultyTab() {
  const t = useT();

  const difficulty = useQuery({
    queryKey: ['report-difficulty'],
    queryFn: () =>
      api<{ data?: DifficultyData } & DifficultyData>('reports/by-difficulty.php').then(
        (r) => r.data ?? (r as unknown as DifficultyData)
      ),
  });

  const data = difficulty.data;
  const summary = data?.difficulty_summary ?? [];
  const totalAll = summary.reduce((s, r) => s + r.total_questions, 0);

  // Pie-style color mapping
  const DIFF_COLORS: Record<string, string> = {
    'ง่าย': 'bg-emerald-500',
    'ปานกลาง': 'bg-amber-400',
    'ยาก': 'bg-orange-500',
    'ยากมาก': 'bg-red-600',
    'ไม่ระบุ': 'bg-slate-300',
  };
  const DIFF_TEXT: Record<string, string> = {
    'ง่าย': 'text-emerald-700',
    'ปานกลาง': 'text-amber-700',
    'ยาก': 'text-orange-700',
    'ยากมาก': 'text-red-700',
    'ไม่ระบุ': 'text-slate-500',
  };

  return (
    <div className="space-y-5">
      {/* Bar chart */}
      <Card>
        <CardHeader>
          <CardTitle>{t('reports.difficulty.title')}</CardTitle>
        </CardHeader>
        <CardBody className="pt-0">
          {difficulty.isLoading ? (
            <SkeletonBars />
          ) : summary.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">{t('reports.difficulty.no_data')}</div>
          ) : (
            <BarList
              items={summary.map((r) => ({ name: r.difficulty_name, value: r.total_questions }))}
              accent="bg-amber-400"
            />
          )}
        </CardBody>
      </Card>

      {/* Percentage breakdown card */}
      {!difficulty.isLoading && summary.length > 0 && totalAll > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('reports.difficulty.breakdown')}</CardTitle>
          </CardHeader>
          <CardBody className="pt-0">
            {/* Stacked bar */}
            <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 mb-5">
              {summary.map((r) => {
                const pct = (r.total_questions / totalAll) * 100;
                if (pct === 0) return null;
                return (
                  <div
                    key={r.difficulty_level}
                    className={DIFF_COLORS[r.difficulty_name] ?? 'bg-slate-300'}
                    style={{ width: `${pct}%` }}
                    title={`${r.difficulty_name}: ${r.total_questions} (${Math.round(pct)}%)`}
                  />
                );
              })}
            </div>
            {/* Legend */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {summary.map((r) => {
                const pct = totalAll ? Math.round((r.total_questions / totalAll) * 100) : 0;
                return (
                  <div key={r.difficulty_level} className="flex items-start gap-2.5">
                    <span
                      className={`mt-1 w-3 h-3 rounded-full flex-shrink-0 ${DIFF_COLORS[r.difficulty_name] ?? 'bg-slate-300'}`}
                    />
                    <div>
                      <div className={`text-sm font-semibold ${DIFF_TEXT[r.difficulty_name] ?? 'text-slate-700'}`}>
                        {r.difficulty_name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {r.total_questions.toLocaleString('th-TH')} ข้อ · {pct}%
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        อนุมัติ {r.approved} · รอ {r.pending}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Error */}
      {difficulty.isError && (
        <Card>
          <CardBody>
            <div className="py-8 text-center text-red-500 text-sm">{t('error.load_failed')}</div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

// ─── Indicator × Grade Tab ────────────────────────────────────────────────────

function IndicatorGradeTab() {
  const t = useT();
  const [subjectId, setSubjectId] = useState('');

  // Subject list comes from metadata, not from the report response — otherwise
  // the dropdown is empty until you pick a subject, which is impossible.
  const meta = useQuery({
    queryKey: ['metadata-full'],
    queryFn: () =>
      api<{ data?: MetaList } & MetaList>('metadata/all.php').then((r) => r.data ?? r),
    staleTime: 5 * 60_000,
  });
  const subjectOptions = meta.data?.subject_groups ?? [];

  const indGrade = useQuery({
    queryKey: ['report-indicator-grade', subjectId],
    queryFn: () => {
      const params = new URLSearchParams();
      if (subjectId) params.set('subject_id', subjectId);
      const qs = params.toString();
      return api<{ data?: IndicatorGradeData } & IndicatorGradeData>(
        `reports/enhanced-indicators-simple.php${qs ? '?' + qs : ''}`
      ).then((r) => r.data ?? (r as unknown as IndicatorGradeData));
    },
    enabled: !!subjectId,
  });

  const data = indGrade.data;
  const allIndicators = data?.allIndicators ?? [];

  // Collect all unique grades present (sorted by grade_name)
  const gradeMap = new Map<number, string>();
  allIndicators.forEach((ind) => {
    if (ind.grade_id) gradeMap.set(ind.grade_id, ind.grade_name);
  });
  // Sort grade ids by name (ป.1, ป.2, ...)
  const gradeIds = Array.from(gradeMap.keys()).sort((a, b) =>
    (gradeMap.get(a) ?? '').localeCompare(gradeMap.get(b) ?? '', 'th')
  );

  // Build pivot: indicator id → grade id → count
  const pivot = new Map<number, { ind: IndicatorForSubject; byGrade: Map<number, number> }>();
  allIndicators.forEach((ind) => {
    if (!pivot.has(ind.id)) {
      pivot.set(ind.id, { ind, byGrade: new Map() });
    }
    pivot.get(ind.id)!.byGrade.set(ind.grade_id, ind.questionCount);
  });

  // Deduplicated indicator rows
  const rows = Array.from(pivot.values());

  function countCellClass(n: number): string {
    if (n === 0) return 'text-slate-300';
    if (n >= 5) return 'text-brand-700 font-bold';
    if (n >= 2) return 'text-brand-600 font-semibold';
    return 'text-brand-500';
  }

  return (
    <div className="space-y-5">
      {/* Subject filter */}
      <Card className="p-4">
        <Select
          label={t('reports.ind_grade.filter_subject')}
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
        >
          <option value="">{t('filter.all_subjects')}</option>
          {subjectOptions.map((s) => (
            <option key={s.id} value={String(s.id)}>{s.name}</option>
          ))}
        </Select>
      </Card>

      {/* Prompt to select subject */}
      {!subjectId && (
        <Card>
          <CardBody>
            <div className="py-12 text-center text-slate-500 text-sm">
              {t('reports.ind_grade.select_subject')}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Loading */}
      {indGrade.isLoading && (
        <Card>
          <CardBody>
            <SkeletonBars />
          </CardBody>
        </Card>
      )}

      {/* Table */}
      {!indGrade.isLoading && subjectId && rows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('reports.ind_grade.title')}</CardTitle>
          </CardHeader>
          <CardBody className="pt-0 overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 sticky left-0 bg-slate-50 min-w-[200px]">
                    {t('reports.ind_grade.col_indicator')}
                  </th>
                  {gradeIds.map((gid) => (
                    <th key={gid} className="px-3 py-2 text-center font-semibold text-slate-600 min-w-[60px]">
                      {gradeMap.get(gid)}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-center font-bold text-slate-700 min-w-[60px]">
                    {t('reports.ind_grade.col_total')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(({ ind, byGrade }) => {
                  const total = Array.from(byGrade.values()).reduce((s, v) => s + v, 0);
                  return (
                    <tr key={ind.id} className="hover:bg-slate-50/60">
                      <td className="px-3 py-2.5 sticky left-0 bg-white">
                        <span className="font-mono text-[10px] text-slate-500 block">{ind.code}</span>
                        <span className="text-slate-700 text-xs">{ind.title}</span>
                      </td>
                      {gradeIds.map((gid) => {
                        const n = byGrade.get(gid) ?? 0;
                        return (
                          <td key={gid} className={`px-3 py-2.5 text-center text-sm ${countCellClass(n)}`}>
                            {n > 0 ? n : '—'}
                          </td>
                        );
                      })}
                      <td className={`px-3 py-2.5 text-center font-bold text-sm ${total > 0 ? 'text-brand-700' : 'text-slate-300'}`}>
                        {total > 0 ? total : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {/* Empty after filtering */}
      {!indGrade.isLoading && subjectId && rows.length === 0 && !indGrade.isError && (
        <Card>
          <CardBody>
            <div className="py-10 text-center text-slate-500 text-sm">
              {t('reports.ind_grade.empty_filter')}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Error */}
      {indGrade.isError && (
        <Card>
          <CardBody>
            <div className="py-8 text-center text-red-500 text-sm">{t('error.load_failed')}</div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

export function Reports() {
  const t = useT();
  const stats = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api<{ data?: DashboardStats } & DashboardStats>('dashboard/stats.php').then((r) => r.data ?? r),
  });

  const STATUS_INFO = [
    { key: 'approved' as const, label: t('reports.status.approved'), icon: CheckCircle2, color: 'bg-emerald-500' },
    { key: 'pending' as const, label: t('reports.status.pending'), icon: Clock, color: 'bg-amber-500' },
    { key: 'draft' as const, label: t('reports.status.draft'), icon: Edit3, color: 'bg-slate-400' },
    { key: 'rejected' as const, label: t('reports.status.rejected'), icon: XCircle, color: 'bg-red-500' },
  ];

  const s = stats.data ?? {};
  const totalByStatus =
    (s.questions_by_status?.approved ?? 0) +
    (s.questions_by_status?.pending ?? 0) +
    (s.questions_by_status?.draft ?? 0) +
    (s.questions_by_status?.rejected ?? 0);

  const overview = (
    <div className="space-y-5">
      {/* Top stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile icon={<FileText />} label={t('reports.stat.total')} value={s.total_questions ?? 0} loading={stats.isLoading} tone="blue" />
        <StatTile icon={<CheckCircle2 />} label={t('reports.stat.approved')} value={s.questions_by_status?.approved ?? 0} loading={stats.isLoading} tone="brand" />
        <StatTile icon={<Clock />} label={t('reports.stat.pending')} value={s.questions_by_status?.pending ?? 0} loading={stats.isLoading} tone="amber" />
        <StatTile icon={<Users />} label={t('reports.stat.users')} value={s.total_users ?? 0} loading={stats.isLoading} tone="violet" />
      </div>

      {/* Status breakdown bar */}
      <Card>
        <CardHeader>
          <CardTitle>{t('reports.status_breakdown')}</CardTitle>
        </CardHeader>
        <CardBody className="pt-0">
          {stats.isLoading ? (
            <Skeleton className="h-3 w-full" />
          ) : totalByStatus === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">{t('reports.no_data')}</div>
          ) : (
            <>
              <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100 mb-4">
                {STATUS_INFO.map((st) => {
                  const v = s.questions_by_status?.[st.key] ?? 0;
                  if (v === 0) return null;
                  return (
                    <div
                      key={st.key}
                      className={st.color}
                      style={{ width: `${(v / totalByStatus) * 100}%` }}
                      title={`${st.label}: ${v}`}
                    />
                  );
                })}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {STATUS_INFO.map((st) => {
                  const v = s.questions_by_status?.[st.key] ?? 0;
                  const pct = totalByStatus ? Math.round((v / totalByStatus) * 100) : 0;
                  const Icon = st.icon;
                  return (
                    <div key={st.key} className="flex items-center gap-2.5">
                      <span className={`w-7 h-7 rounded-md ${st.color}/10 flex items-center justify-center`}>
                        <Icon className={`w-3.5 h-3.5 text-slate-600`} />
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{v.toLocaleString('th-TH')}</div>
                        <div className="text-[11px] text-slate-500">{st.label} · {pct}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );

  const bySubject = (
    <Card>
      <CardHeader>
        <CardTitle>{t('reports.by_subject_title')}</CardTitle>
      </CardHeader>
      <CardBody className="pt-0">
        {stats.isLoading ? (
          <SkeletonBars />
        ) : (s.questions_by_subject ?? []).length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">{t('reports.no_data')}</div>
        ) : (
          <BarList items={(s.questions_by_subject ?? []).map((x) => ({ name: x.name, value: x.count }))} />
        )}
      </CardBody>
    </Card>
  );

  const byGrade = (
    <Card>
      <CardHeader>
        <CardTitle>{t('reports.by_grade_title')}</CardTitle>
      </CardHeader>
      <CardBody className="pt-0">
        {stats.isLoading ? (
          <SkeletonBars />
        ) : (s.questions_by_grade ?? []).length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">{t('reports.no_data')}</div>
        ) : (
          <BarList items={(s.questions_by_grade ?? []).map((x) => ({ name: x.name, value: x.count }))} accent="bg-blue-500" />
        )}
      </CardBody>
    </Card>
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('reports.title')}
        description={t('reports.desc')}
      />

      <Tabs
        variant="pills"
        tabs={[
          { id: 'overview',             label: t('reports.tab.overview'),              icon: <TrendingUp className="w-4 h-4" />, content: overview },
          { id: 'subject',              label: t('reports.tab.by_subject'),            icon: <BarChart3 className="w-4 h-4" />,  content: bySubject },
          { id: 'grade',                label: t('reports.tab.by_grade'),              icon: <BarChart3 className="w-4 h-4" />,  content: byGrade },
          { id: 'cognitive',            label: t('reports.tab.by_cognitive'),          icon: <Brain className="w-4 h-4" />,      content: <CognitiveTab /> },
          { id: 'difficulty',           label: t('reports.tab.by_difficulty'),         icon: <Layers className="w-4 h-4" />,     content: <DifficultyTab /> },
          { id: 'indicators_by_grade',  label: t('reports.tab.indicators_by_grade'),  icon: <FileText className="w-4 h-4" />,   content: <IndicatorGradeTab /> },
          { id: 'coverage',             label: t('reports.tab.coverage'),              icon: <BookOpen className="w-4 h-4" />,   content: <CoverageTab /> },
        ]}
      />
    </div>
  );
}

interface StatTileProps {
  icon: React.ReactNode; label: string; value: number; loading?: boolean;
  tone?: 'brand' | 'amber' | 'blue' | 'red' | 'violet';
}
const TILE_TONE = {
  brand: 'bg-brand-50 text-brand-600',
  amber: 'bg-amber-50 text-amber-600',
  blue: 'bg-blue-50 text-blue-600',
  red: 'bg-red-50 text-red-600',
  violet: 'bg-violet-50 text-violet-600',
};
function StatTile({ icon, label, value, loading, tone = 'brand' }: StatTileProps) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-3.5">
        <span className={`w-11 h-11 rounded-lg flex items-center justify-center ${TILE_TONE[tone]}`}>
          <span className="w-5 h-5 [&>svg]:w-5 [&>svg]:h-5">{icon}</span>
        </span>
        <div>
          {loading ? <Skeleton className="w-14 h-7 mb-1" /> : (
            <div className="text-2xl font-bold text-slate-900 leading-none">
              {value.toLocaleString('th-TH')}
            </div>
          )}
          <div className="text-xs text-slate-500 mt-1.5">{label}</div>
        </div>
      </div>
    </Card>
  );
}

interface BarListItem { name: string; value: number; }
function BarList({ items, accent = 'bg-brand-500' }: { items: BarListItem[]; accent?: string }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.name}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-medium text-slate-700 truncate max-w-[70%]">{it.name}</span>
            <Badge tone="neutral">{it.value.toLocaleString('th-TH')}</Badge>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={accent}
              style={{ width: `${Math.max(2, (it.value / max) * 100)}%`, height: '100%' }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function SkeletonBars() {
  return (
    <ul className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i}>
          <Skeleton className="h-3 w-1/3 mb-2" />
          <Skeleton className="h-2 w-full" />
        </li>
      ))}
    </ul>
  );
}
